import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../cn';

/**
 * Branded Button primitive. Same Radix-Slot + cva pattern as the shadcn set
 * under apps/mas/src/components/ui, re-homed into the design system so the
 * portal modules render with ONE token-themeable button (it reads --primary /
 * --accent / --ring, so it re-themes per tenant with no module change).
 */
export const buttonVariants = cva(
  /*
   * R1 metabus-site-and-portal-view iteration 2 fix (site-visual-reviewer
   * finding #4): `disabled:opacity-50` alone read as visually identical to
   * the enabled state at screenshot resolution against a light-primary fill
   * (e.g. the social portal's Approve button while a fail verdict blocks
   * it). Disabled now ALSO swaps to a flat neutral (`disabled:bg-muted
   * disabled:text-muted-foreground`), drops the shadow, and shows
   * `cursor-not-allowed` — three simultaneous, unambiguous cues (grey fill,
   * reduced opacity, disabled cursor) instead of relying on opacity alone.
   */
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-border bg-background shadow-sm hover:bg-muted hover:text-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        accent: 'bg-accent text-accent-foreground shadow hover:bg-accent/90',
        ghost: 'hover:bg-muted hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';
